import {
  type BodyMeasurementAxisRange,
  formatMeasurementValue,
  type WeeklyAggregate,
} from "@/lib/body-measurements";

export interface ChartLayout {
  width: number;
  height: number;
  plotLeft: number;
  plotRight: number;
  plotTop: number;
  plotBottom: number;
}

/**
 * Fixed chart height in CSS pixels — the same at every width, because the
 * viewBox is sized to the measured container rather than the other way
 * round. A third shorter than the original 280.
 *
 * Mirrored by `.trend-chart-wrap`'s `min-height`, which reserves this space
 * before the first measurement lands so the card does not jump.
 */
const CHART_HEIGHT = 188;

/** Room for the last week's value label to sit centred without clipping. */
const RIGHT_MARGIN = 52;
/**
 * Was 26 to reserve a row for a separate unit label; with the unit on every
 * tick that row goes back to the plot, making the spread whiskers taller.
 */
const PLOT_TOP = 16;
const PLOT_BOTTOM = 160;
/**
 * Below this the plot would invert (`plotRight < plotLeft`). Narrower cards
 * scale the whole chart down instead, which is ugly but never broken.
 */
const MINIMUM_WIDTH = 260;

const AXIS_LABEL_FONT_SIZE = 11;
/** Geist Mono advances 0.6em per glyph, so every character is this wide. */
const AXIS_LABEL_CHARACTER_WIDTH = AXIS_LABEL_FONT_SIZE * 0.6;
/** Matches the tick text's own x offset from `plotLeft`. */
const AXIS_LABEL_GAP = 6;
/** Keeps the longest label clear of the card's left edge. */
const AXIS_GUTTER_MARGIN = 4;
const MINIMUM_AXIS_GUTTER = 28;

/**
 * Width to reserve for the y axis, measured from the labels it will actually
 * hold rather than the worst case.
 *
 * A fixed gutter has to be wide enough for `"102,5 cm"` (~63px), but a weight
 * axis only ever shows `"85 kg"` (~43px). On a 322px phone card that spent a
 * fifth of the chart on empty gutter; at 1:1 that is a fifth of the *plot*,
 * since nothing scales down to hide it any more.
 */
export function axisGutter(scale: AxisScale | null, unit: string): number {
  if (scale === null) return MINIMUM_AXIS_GUTTER;
  const longest = Math.max(
    ...scale.ticks.map(
      (tick) => axisTickLabel(tick, scale.decimals, unit).length,
    ),
  );
  return Math.max(
    MINIMUM_AXIS_GUTTER,
    Math.ceil(longest * AXIS_LABEL_CHARACTER_WIDTH) +
      AXIS_LABEL_GAP +
      AXIS_GUTTER_MARGIN,
  );
}

/**
 * Geometry for a chart rendered at 1:1 — **one viewBox unit is one CSS
 * pixel**, because the viewBox width is the container's measured width.
 *
 * The chart used to be a fixed 640-unit viewBox at `width: 100%`, which
 * scaled every mark with the container: 0.50× on a phone, 3.50× on a 2560px
 * monitor, so axis text ranged from 5.5px to 38px. Sizing the viewBox to the
 * container instead pins text, strokes, dots and whiskers to real pixels and
 * lets only the week spacing stretch. The cost is that label density no
 * longer scales along with everything else — see `labelStride`.
 */
/**
 * A y-axis tick as drawn: the value and its unit, or the bare value on a
 * unitless scale such as Smertenivå.
 */
export function axisTickLabel(
  tick: number,
  decimals: number,
  unit: string,
): string {
  const value = formatMeasurementValue(tick, decimals);
  return unit ? `${value} ${unit}` : value;
}

export function chartLayout(width: number, gutter: number): ChartLayout {
  const usable = Math.max(width, MINIMUM_WIDTH);
  return {
    width: usable,
    height: CHART_HEIGHT,
    plotLeft: gutter,
    plotRight: usable - RIGHT_MARGIN,
    plotTop: PLOT_TOP,
    plotBottom: PLOT_BOTTOM,
  };
}

/** Rendered width of "Uke NN" at 11px mono, plus breathing room. */
export const WEEK_LABEL_WIDTH = 54;
/** Rendered width of a value label like "102,5" at 9px mono, plus room. */
export const VALUE_LABEL_WIDTH = 34;

/**
 * Label every nth week, so labels never collide at any container width.
 *
 * At a fixed viewBox this was a constant "every 2nd", which worked only
 * because labels shrank with the plot. At 1:1 the columns shrink and the
 * text does not, so the stride has to come from the column width: wide
 * cards end up labelling every week, a phone every third.
 */
export function labelStride(
  count: number,
  layout: ChartLayout,
  labelWidth: number,
): number {
  const column = (layout.plotRight - layout.plotLeft) / count;
  if (column <= 0) return count;
  return Math.max(1, Math.ceil(labelWidth / column));
}

/** True when `index` falls on a stride slot, counting back from the last. */
export function isLabelled(
  index: number,
  count: number,
  stride: number,
): boolean {
  return (count - 1 - index) % stride === 0;
}

export interface AxisDomain {
  minimum: number;
  maximum: number;
}

export interface AxisScale {
  minimum: number;
  maximum: number;
  step: number;
  /** Ascending, evenly spaced; `ticks[0] === minimum`. */
  ticks: number[];
  /** Decimals the step needs, so tick labels don't round away detail. */
  decimals: number;
}

const EPSILON = 1e-9;

const MINIMUM_INTERVALS = 4;
const MAXIMUM_INTERVALS = 6;
const PREFERRED_INTERVALS = 5;

const NICE_STEPS: number[] = [];
for (let exponent = -3; exponent <= 4; exponent++) {
  for (const mantissa of [1, 2, 2.5, 5]) {
    NICE_STEPS.push(mantissa * 10 ** exponent);
  }
}
NICE_STEPS.sort((a, b) => a - b);

function decimalsOf(step: number): number {
  const text = String(step);
  const dot = text.indexOf(".");
  return dot === -1 ? 0 : text.length - dot - 1;
}

function roundToStep(value: number, step: number): number {
  const factor = 10 ** Math.min(10, decimalsOf(step) + 2);
  return Math.round(value * factor) / factor;
}

/**
 * What the axis must show: the series' min–max across the window, widened
 * by any configured range. A configured range is a floor, never a clip — a
 * real value outside it extends the axis rather than disappearing.
 * Null when there is neither data nor a configured range.
 *
 * Deliberately unpadded: `axisScale` expands outward to the next round
 * step, which supplies the headroom the old ±5% padding used to.
 */
export function requiredDomain(
  aggregates: Array<WeeklyAggregate | null>,
  configured: BodyMeasurementAxisRange | undefined,
): AxisDomain | null {
  const measured = aggregates.filter((a): a is WeeklyAggregate => a !== null);
  const hasConfigured =
    configured !== undefined &&
    Number.isFinite(configured.minimum) &&
    Number.isFinite(configured.maximum);
  if (measured.length === 0) {
    return hasConfigured
      ? { minimum: configured.minimum, maximum: configured.maximum }
      : null;
  }
  let minimum = Math.min(...measured.map((a) => a.minimum));
  let maximum = Math.max(...measured.map((a) => a.maximum));
  if (hasConfigured) {
    minimum = Math.min(configured.minimum, minimum);
    maximum = Math.max(configured.maximum, maximum);
  }
  // A perfectly flat series would otherwise ask for a zero-height axis.
  if (maximum - minimum < EPSILON) {
    minimum -= 1;
    maximum += 1;
  }
  return { minimum, maximum };
}

/**
 * Smallest round step that covers `required` in exactly `intervals`
 * divisions, aligned so every tick is a multiple of the step.
 */
function fitAxis(required: AxisDomain, intervals: number): AxisScale | null {
  for (const step of NICE_STEPS) {
    const minimum = roundToStep(
      Math.floor(required.minimum / step + EPSILON) * step,
      step,
    );
    const maximum = roundToStep(minimum + intervals * step, step);
    if (maximum + EPSILON < required.maximum) continue;
    return {
      minimum,
      maximum,
      step,
      decimals: decimalsOf(step),
      ticks: Array.from({ length: intervals + 1 }, (_, i) =>
        roundToStep(minimum + i * step, step),
      ),
    };
  }
  return null;
}

/**
 * Round-numbered scale for the axis: whichever division count between four
 * and six wastes the least headroom, ties going to the count nearest five.
 */
export function axisScale(required: AxisDomain): AxisScale {
  let best: AxisScale | null = null;
  let bestIntervals = PREFERRED_INTERVALS;
  let bestSlack = Number.POSITIVE_INFINITY;
  const span = required.maximum - required.minimum || 1;
  for (
    let intervals = MINIMUM_INTERVALS;
    intervals <= MAXIMUM_INTERVALS;
    intervals++
  ) {
    const scale = fitAxis(required, intervals);
    if (scale === null) continue;
    const slack = (scale.maximum - scale.minimum - span) / span;
    const closerToPreferred =
      Math.abs(intervals - PREFERRED_INTERVALS) <
      Math.abs(bestIntervals - PREFERRED_INTERVALS);
    if (
      slack < bestSlack - EPSILON ||
      (Math.abs(slack - bestSlack) <= EPSILON && closerToPreferred)
    ) {
      best = scale;
      bestIntervals = intervals;
      bestSlack = slack;
    }
  }
  if (best !== null) return best;
  // Unreachable for realistic body measurements — the step ladder reaches
  // 50 000 — but an axis is better than a crash.
  const step = span / PREFERRED_INTERVALS;
  return {
    minimum: required.minimum,
    maximum: required.maximum,
    step,
    decimals: 1,
    ticks: Array.from(
      { length: PREFERRED_INTERVALS + 1 },
      (_, i) => required.minimum + i * step,
    ),
  };
}

/** Gridline y positions, one per tick, bottom first. */
export function gridlinePositions(
  scale: AxisScale,
  layout: ChartLayout,
): number[] {
  const intervals = scale.ticks.length - 1;
  const height = layout.plotBottom - layout.plotTop;
  return Array.from(
    { length: scale.ticks.length },
    (_, i) => layout.plotBottom - (i / intervals) * height,
  );
}

export function xPosition(
  index: number,
  count: number,
  layout: ChartLayout,
): number {
  const step = (layout.plotRight - layout.plotLeft) / count;
  return layout.plotLeft + step * (index + 0.5);
}

function yPosition(
  value: number,
  scale: AxisScale,
  layout: ChartLayout,
): number {
  const span = scale.maximum - scale.minimum || 1;
  const t = (value - scale.minimum) / span;
  return layout.plotBottom - t * (layout.plotBottom - layout.plotTop);
}

export interface SeriesPoint {
  index: number;
  x: number;
  /** The week's average — where the dot sits. */
  y: number;
  /** The week's maximum, i.e. the upper cap of its spread whisker. */
  top: number;
  /** The week's minimum, i.e. the lower cap of its spread whisker. */
  bottom: number;
  value: number;
  /** False when a week's measurements were all equal — no whisker to draw. */
  hasSpread: boolean;
}

export interface SeriesGeometry {
  /** One path per consecutive measured run of ≥2 weeks; gaps break the line. */
  linePaths: string[];
  /** Every measured week, including isolated ones. */
  points: SeriesPoint[];
}

export function seriesGeometry(
  aggregates: Array<WeeklyAggregate | null>,
  scale: AxisScale,
  layout: ChartLayout,
): SeriesGeometry {
  const count = aggregates.length;
  const runs: number[][] = [];
  let run: number[] = [];
  aggregates.forEach((a, i) => {
    if (a === null) {
      if (run.length > 0) runs.push(run);
      run = [];
      return;
    }
    run.push(i);
  });
  if (run.length > 0) runs.push(run);

  const linePaths: string[] = [];
  const points: SeriesPoint[] = [];

  for (const indices of runs) {
    for (const i of indices) {
      const aggregate = aggregates[i] as WeeklyAggregate;
      points.push({
        index: i,
        x: xPosition(i, count, layout),
        y: yPosition(aggregate.average, scale, layout),
        top: yPosition(aggregate.maximum, scale, layout),
        bottom: yPosition(aggregate.minimum, scale, layout),
        value: aggregate.average,
        hasSpread: aggregate.maximum - aggregate.minimum > EPSILON,
      });
    }
    if (indices.length < 2) continue;
    linePaths.push(
      indices
        .map((i, order) => {
          const aggregate = aggregates[i] as WeeklyAggregate;
          const x = round(xPosition(i, count, layout));
          const y = round(yPosition(aggregate.average, scale, layout));
          return `${order === 0 ? "M" : "L"}${x},${y}`;
        })
        .join(" "),
    );
  }

  return { linePaths, points };
}

const LABEL_CLEARANCE = 5;
const LABEL_LINE = 9;

/**
 * Baseline y per week for the value labels: clear of that week's spread
 * whisker, and flipped under the whisker rather than escaping the plot
 * when a point sits at the top of the axis.
 */
export function valueLabelPositions(
  points: SeriesPoint[],
  layout: ChartLayout,
): Map<number, number> {
  const ceiling = layout.plotTop - 4;
  const floor = layout.plotBottom + LABEL_LINE + 2;
  const positions = new Map<number, number>();
  for (const point of points) {
    const above = point.top - LABEL_CLEARANCE;
    const below = point.bottom + LABEL_CLEARANCE + LABEL_LINE;
    positions.set(
      point.index,
      Math.min(floor, above < ceiling ? below : above),
    );
  }
  return positions;
}

function round(n: number): number {
  return Math.round(n * 10) / 10;
}
